/**
 * Insurance data access: TanStack Query hooks over the typed client. Risk reads are
 * safety-only and pseudonymised by the server for anyone without `fleet.driver.identity`.
 */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, unwrap } from '@/lib/api/client';
import type { ActionOut, ClaimFromDetection, ClaimPatch, ClaimStatus, CreateClaim, EvidenceIn } from './types';

export const insuranceKeys = {
  all: ['insurance'] as const,
  risk: (p: object) => ['insurance', 'risk', p] as const,
  model: ['insurance', 'model'] as const,
  trend: (p: object) => ['insurance', 'trend', p] as const,
  drivers: (p: object) => ['insurance', 'drivers', p] as const,
  segments: (p: object) => ['insurance', 'segments', p] as const,
  coaching: (p: object) => ['insurance', 'coaching', p] as const,
  claims: (p: object) => ['insurance', 'claims', p] as const,
  claim: (id: string) => ['insurance', 'claim', id] as const,
};

/** A local-day window: `end` inclusive (the insights convention). */
export interface Period {
  start: string;
  end: string;
}

export function useFleetRisk(p: Period & { project?: string[] }, enabled = true) {
  return useQuery({
    queryKey: insuranceKeys.risk(p),
    queryFn: () => unwrap(api.GET('/api/insurance/risk', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useRiskModel(enabled = true) {
  return useQuery({ queryKey: insuranceKeys.model, queryFn: () => unwrap(api.GET('/api/insurance/risk/model')), staleTime: 60 * 60_000, enabled });
}

export function useRiskTrend(p: { project?: string[]; granularity: 'week' | 'month' }, enabled = true) {
  return useQuery({
    queryKey: insuranceKeys.trend(p),
    queryFn: () => unwrap(api.GET('/api/insurance/risk/trend', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export interface DriverRiskQuery extends Period {
  project?: string[];
  order?: 'worst' | 'best';
  band?: 'low' | 'moderate' | 'elevated' | 'high';
  limit?: number;
}

export function useDriverRisks(p: DriverRiskQuery, enabled = true) {
  return useQuery({
    queryKey: insuranceKeys.drivers(p),
    queryFn: () => unwrap(api.GET('/api/insurance/risk/drivers', { params: { query: { ...p, limit: p.limit ?? 500 } } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSegments(p: Period & { dimension: string }, enabled = true) {
  return useQuery({
    queryKey: insuranceKeys.segments(p),
    queryFn: () => unwrap(api.GET('/api/insurance/risk/segments', { params: { query: p } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCoachingImpact(p: Period & { project?: string[] }, enabled = true) {
  return useQuery({
    queryKey: insuranceKeys.coaching(p),
    queryFn: () => unwrap(api.GET('/api/insurance/coaching-impact', { params: { query: p } })),
    enabled,
  });
}

export interface ClaimQuery {
  project?: string[];
  status?: ClaimStatus;
  vehicle?: string;
  driver?: string;
  since?: string;
  until?: string;
}

export function useClaims(q: ClaimQuery, enabled = true) {
  return useInfiniteQuery({
    queryKey: insuranceKeys.claims(q),
    queryFn: ({ pageParam }) => unwrap(api.GET('/api/insurance/claims', { params: { query: { ...q, cursor: pageParam, limit: 100 } } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useClaim(id: string) {
  return useQuery({
    queryKey: insuranceKeys.claim(id),
    queryFn: () => unwrap(api.GET('/api/insurance/claims/{claim_id}', { params: { path: { claim_id: id } } })),
  });
}

function useAction<V>(fn: (v: V) => Promise<ActionOut>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: insuranceKeys.all }) });
}

export function useCreateClaim() {
  return useAction((body: CreateClaim) => unwrap(api.POST('/api/insurance/claims', { body })));
}

export function useFnol() {
  return useAction((body: ClaimFromDetection) => unwrap(api.POST('/api/insurance/claims/fnol', { body })));
}

export function useUpdateClaim(id: string) {
  return useAction((body: ClaimPatch) => unwrap(api.PATCH('/api/insurance/claims/{claim_id}', { params: { path: { claim_id: id } }, body })));
}

export function useSubmitClaim(id: string) {
  return useAction((version: number) => unwrap(api.POST('/api/insurance/claims/{claim_id}/submit', { params: { path: { claim_id: id } }, body: { version } })));
}

export function useDecideClaim(id: string) {
  return useAction(
    (body: { decision: 'accepted' | 'rejected'; reason: string; cost_final_eur?: number | null; insurer_ref?: string | null; version: number }) =>
      unwrap(api.POST('/api/insurance/claims/{claim_id}/decide', { params: { path: { claim_id: id } }, body })),
  );
}

export function useClaimStatus(id: string) {
  return useAction((body: { status: 'closed' | 'open'; reason: string; version: number }) =>
    unwrap(api.POST('/api/insurance/claims/{claim_id}/status', { params: { path: { claim_id: id } }, body })),
  );
}

export function useAddEvidence(id: string) {
  return useAction((body: EvidenceIn) => unwrap(api.POST('/api/insurance/claims/{claim_id}/evidence', { params: { path: { claim_id: id } }, body })));
}

/** Recent detections (telemetry's list) to report a claim from — first notice of loss. */
export function useRecentDetections(p: { vehicle?: string; start: string }, enabled: boolean) {
  return useQuery({
    queryKey: ['insurance', 'detections', p],
    queryFn: () => unwrap(api.GET('/api/detections', { params: { query: { vehicle: p.vehicle, start: p.start, limit: 100 } } })),
    enabled,
  });
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Server CSV exports (pseudonymised and CSV-injection safe server-side). Returns whether rows were cut off. */
export async function downloadExport(which: 'drivers' | 'claims', query: Record<string, string | string[] | undefined>): Promise<boolean> {
  const call =
    which === 'drivers'
      ? api.GET('/api/insurance/exports/drivers.csv', { params: { query }, parseAs: 'blob' })
      : api.GET('/api/insurance/exports/claims.csv', { params: { query: query as { status?: ClaimStatus } }, parseAs: 'blob' });
  const { data, error, response } = await call;
  if (error !== undefined || !response.ok || !data) {
    const detail = error && typeof error === 'object' && 'detail' in error ? (error as { detail: unknown }).detail : undefined;
    throw new ApiError(response.status, detail);
  }
  const name = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') ?? '')?.[1] ?? `${which}.csv`;
  saveBlob(data as Blob, name);
  return response.headers.get('X-Truncated') === 'true';
}
